import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  boxStyle,
  cardStyle,
  mainOptions,
} from "../Component/ShiftCategoryOptions";
import { CustomButton } from "components/CustomComponents";
import {
  Box,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import { themes } from "services/constants";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import {
  prefixSuffixRuleForWeekOff,
  prefixSuffixRuleForHoliday,
} from "../Component/ShiftCategoryOptions";
import { WorkAdjustmentPriority } from "./Deductions/WorkAdjustmentPriority";

const PRIORITY_PENALTY_OPTIONS = [
  { id: "Break Penalty", label: "Break Penalty", type: "Break" },
  { id: "Absence Penalty", label: "Absence Penalty", type: "Absent" },
  { id: "Shift Penalty", label: "Shift Penalty", type: "Shift" },
  { id: "Permission Penalty", label: "Permission Penalty", type: "Permission" },
];

const getAbsencePriorityType = (name = "") =>
  PRIORITY_PENALTY_OPTIONS.find(
    (option) => option.label === name || option.id === name,
  )?.type ?? "";

const normalizeAbsenceWorkAdjustmentRows = (rows = []) =>
  Array.isArray(rows)
    ? rows
        .map((row) => ({
          band_id: row?.band_id ? String(row.band_id) : "",
          band_name: row?.band_name ?? "",
          factor: row?.factor ?? row?.band_factor ?? "",
          source: row?.source ?? "",
          band_order: row?.band_order ?? row?.order,
          priorities: Array.isArray(row?.priorities)
            ? row.priorities
                .map((priority) => ({
                  name: priority?.name ?? priority?.priority ?? "",
                  type:
                    priority?.type ??
                    getAbsencePriorityType(
                      priority?.name ?? priority?.priority ?? "",
                    ),
                }))
                .filter((priority) => priority.name)
            : Array.isArray(row?.penalty_assignments)
              ? row.penalty_assignments
                  .map((priority) => ({
                    name: priority?.penalty_type ?? "",
                    type: getAbsencePriorityType(priority?.penalty_type ?? ""),
                  }))
                  .filter((priority) => priority.name)
              : [],
        }))
        .filter((row) => row.band_id)
    : [];

const buildPriorityRowsFromOptions = (options = []) =>
  options.map((option) => ({
    name: option.label,
    type: option.type,
  }));

// Only drops priorities whose penalty type has since been disabled — it
// never adds a newly-enabled type to a row the user already configured.
// Picking up new penalty types automatically is reserved for a band's
// first-time creation (see createDefaultAbsenceWorkAdjustmentRow), not for
// rows a user has already set up with a specific, intentional priority list.
const removeDisabledPriorities = (priorities = [], enabledOptions = []) => {
  const enabledNames = new Set(enabledOptions.map((option) => option.label));
  return Array.isArray(priorities)
    ? priorities.filter((priority) => enabledNames.has(priority.name))
    : [];
};

export const Adjustment = ({
  onUpdate,
  handleNext,
  handleBack,
  data,
  deductionsData,
  earningData,
  complianceData,
  paymentBasedOn,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: overtimeBands } = useFetch(
    domain
      ? `${domain}administration/shiftroster/shiftrota/overtime-bands/`
      : "",
  );
  const [absenceWorkAdjustmentRows, setAbsenceWorkAdjustmentRows] = useState(
    [],
  );
  const isBandAllowed = complianceData?.is_band_allowed === true;
  const [openAbsenceWorkAdjustmentDialog, setOpenAbsenceWorkAdjustmentDialog] =
    useState(false);
  const [
    editingAbsenceWorkAdjustmentIndex,
    setEditingAbsenceWorkAdjustmentIndex,
  ] = useState(null);
  const [absenceWorkAdjustmentDraft, setAbsenceWorkAdjustmentDraft] = useState({
    band_id: "",
    band_name: "",
    factor: "",
    source: "",
    priorities: [],
  });
  const [absenceWorkAdjustmentErrors, setAbsenceWorkAdjustmentErrors] =
    useState({
      band_id: false,
      priorities: false,
    });
  const { handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      is_onduty_allowed: false,
      is_permission_allowed: false,
      is_permission_include_working_hour: false,
      is_compoff_allowed: false,
      is_weekoff_sandwich_leave: false,
      absence_rule_type: "Before",
      is_holiday_sandwich_leave: false,
      absence_rule_type_for_holiday: "Before",
      is_work_adjustment_priority: false,
      is_permission_hours_included_in_the_priority_deduction: false,
    },
  });
  const isPermissionAllowed = watch(
    "is_permission_hours_included_in_the_priority_deduction",
  );
  const enabledPriorityPenaltyOptions = useMemo(() => {
    const options = [];

    if (deductionsData?.is_apply_break_penalty_policy) {
      options.push(PRIORITY_PENALTY_OPTIONS[0]);
    }

    if (deductionsData?.deduct_salary_for_absence) {
      options.push(PRIORITY_PENALTY_OPTIONS[1]);
    }

    if (deductionsData?.is_apply_sft_penalty_policy) {
      options.push(PRIORITY_PENALTY_OPTIONS[2]);
    }

    if (isPermissionAllowed) {
      options.push(PRIORITY_PENALTY_OPTIONS[3]);
    }

    return options;
  }, [
    deductionsData?.deduct_salary_for_absence,
    deductionsData?.is_apply_break_penalty_policy,
    deductionsData?.is_apply_sft_penalty_policy,
    isPermissionAllowed,
  ]);

  const absenceWorkAdjustmentBandOptions = useMemo(() => {
    const normalizedBands = Array.isArray(overtimeBands)
      ? overtimeBands
      : Array.isArray(overtimeBands?.responseDetails)
        ? overtimeBands.responseDetails
        : [];

    return normalizedBands
      .map((row, index) => {
        const bandName = row?.band_name?.trim?.() || "Unnamed Band";
        const factor =
          row?.band_factor ?? row?.factor ?? row?.payment_factor ?? "";
        const normalizedFactor = factor === "" ? "" : String(factor);

        return {
          id: String(row?.id ?? `ot-band-${index}`),
          label: bandName,
          band_name: bandName,
          factor: normalizedFactor,
          source: "OT",
        };
      })
      .filter((row) => row.band_name);
  }, [overtimeBands]);

  const createDefaultAbsenceWorkAdjustmentRow = useCallback(() => {
    const defaultBand =
      absenceWorkAdjustmentBandOptions.find((band) =>
        ["attendance", "shift earning"].includes(
          band.band_name?.trim().toLowerCase(),
        ),
      ) ?? absenceWorkAdjustmentBandOptions[0];

    if (!defaultBand) {
      return null;
    }

    return {
      band_id: String(defaultBand.id),
      band_name: defaultBand.band_name ?? "",
      factor: defaultBand.factor ?? "",
      source: defaultBand.source ?? "",
      priorities: buildPriorityRowsFromOptions(enabledPriorityPenaltyOptions),
    };
  }, [absenceWorkAdjustmentBandOptions, enabledPriorityPenaltyOptions]);

  // Guards the priority-sync effect below from running before the saved
  // `data` (and the form fields it drives, like
  // is_permission_hours_included_in_the_priority_deduction) has actually
  // been applied via reset(). Without this, enabledPriorityPenaltyOptions
  // can momentarily compute against stale/default form values on first
  // render, and the sync effect would wrongly strip a penalty type (e.g.
  // Permission Penalty) that's really still enabled once `data` loads.
  const [isAdjustmentDataReady, setIsAdjustmentDataReady] = useState(false);

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset(data);
      const absenceWorkAdjustment = normalizeAbsenceWorkAdjustmentRows(
        data?.absence_work_adjustment_priority_bands ?? [],
      );

      setAbsenceWorkAdjustmentRows(absenceWorkAdjustment);
      setIsAdjustmentDataReady(true);
    }
  }, [data, reset]);

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      return;
    }

    // No saved data to wait for (a brand-new category), so the sync effect
    // is safe to run immediately.
    setIsAdjustmentDataReady(true);

    if (absenceWorkAdjustmentRows.length > 0) {
      return;
    }

    const defaultRow = createDefaultAbsenceWorkAdjustmentRow();

    if (defaultRow) {
      setAbsenceWorkAdjustmentRows([defaultRow]);
    }
  }, [
    absenceWorkAdjustmentRows.length,
    createDefaultAbsenceWorkAdjustmentRow,
    data,
  ]);

  useEffect(() => {
    if (!isAdjustmentDataReady) {
      return;
    }

    if (enabledPriorityPenaltyOptions.length === 0) {
      setValue("is_work_adjustment_priority", false);
      setAbsenceWorkAdjustmentRows([]);
      setAbsenceWorkAdjustmentDraft((prev) => ({
        ...prev,
        priorities: [],
      }));
      return;
    }

    if (absenceWorkAdjustmentRows.length === 0) {
      const defaultRow = createDefaultAbsenceWorkAdjustmentRow();

      if (defaultRow) {
        setAbsenceWorkAdjustmentRows([defaultRow]);
      }
    }

    setAbsenceWorkAdjustmentRows((prev) =>
      prev.map((row) => {
        return {
          ...row,
          priorities: removeDisabledPriorities(
            row?.priorities,
            enabledPriorityPenaltyOptions,
          ),
        };
      }),
    );

    setAbsenceWorkAdjustmentDraft((prev) => {
      return {
        ...prev,
        priorities: removeDisabledPriorities(
          prev?.priorities,
          enabledPriorityPenaltyOptions,
        ),
      };
    });
  }, [
    isAdjustmentDataReady,
    absenceWorkAdjustmentRows.length,
    createDefaultAbsenceWorkAdjustmentRow,
    setValue,
    enabledPriorityPenaltyOptions,
  ]);

  const onSubmit = async (formValues) => {
    onUpdate({
      ...formValues,
      absence_work_adjustment_priority_bands: absenceWorkAdjustmentRows,
    });
    handleNext(4);
  };

  const handlePrefixSuffixForWeekoff = (event) => {
    setValue("absence_rule_type", event.target.value);
  };

  const handlePrefixSuffixForHoliday = (event) => {
    setValue("absence_rule_type_for_holiday", event.target.value);
  };

  const getAvailablePenaltyOptionsForDraft = useCallback(
    (priorities) =>
      enabledPriorityPenaltyOptions.filter(
        (option) =>
          !priorities.some((priority) => priority.name === option.label),
      ),
    [enabledPriorityPenaltyOptions],
  );

  const validateAbsenceWorkAdjustmentDraft = useCallback(() => {
    const nextErrors = {
      band_id: !absenceWorkAdjustmentDraft.band_id,
      priorities: absenceWorkAdjustmentDraft.priorities.length === 0,
    };

    setAbsenceWorkAdjustmentErrors(nextErrors);
    return !nextErrors.band_id && !nextErrors.priorities;
  }, [absenceWorkAdjustmentDraft]);

  const openAddAbsenceWorkAdjustmentDialog = () => {
    setEditingAbsenceWorkAdjustmentIndex(null);
    setAbsenceWorkAdjustmentDraft({
      band_id: "",
      band_name: "",
      factor: "",
      source: "",
      priorities: [],
    });
    setAbsenceWorkAdjustmentErrors({
      band_id: false,
      priorities: false,
    });
    setOpenAbsenceWorkAdjustmentDialog(true);
  };

  const openEditAbsenceWorkAdjustmentDialog = (index) => {
    setEditingAbsenceWorkAdjustmentIndex(index);
    setAbsenceWorkAdjustmentDraft({
      ...absenceWorkAdjustmentRows[index],
      priorities:
        absenceWorkAdjustmentRows[index]?.priorities?.map((item) => ({
          ...item,
        })) ?? [],
    });
    setAbsenceWorkAdjustmentErrors({
      band_id: false,
      priorities: false,
    });
    setOpenAbsenceWorkAdjustmentDialog(true);
  };

  const handleAbsenceWorkAdjustmentBandChange = (selectedOption) => {
    setAbsenceWorkAdjustmentDraft((prev) => ({
      ...prev,
      band_id: selectedOption?.id ? String(selectedOption.id) : "",
      band_name: selectedOption?.band_name ?? "",
      factor: selectedOption?.factor ?? "",
      source: selectedOption?.source ?? "",
    }));
    setAbsenceWorkAdjustmentErrors((prev) => ({
      ...prev,
      band_id: false,
    }));
  };

  const handleAddPenaltyToAbsenceDraft = (penaltyOption) => {
    if (!penaltyOption) {
      return;
    }

    setAbsenceWorkAdjustmentDraft((prev) => ({
      ...prev,
      priorities: [
        ...prev.priorities,
        { name: penaltyOption.label, type: penaltyOption.type },
      ],
    }));
    setAbsenceWorkAdjustmentErrors((prev) => ({
      ...prev,
      priorities: false,
    }));
  };

  const handleMoveAbsenceDraftPriority = (index, direction) => {
    setAbsenceWorkAdjustmentDraft((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.priorities.length) {
        return prev;
      }

      const nextPriorities = [...prev.priorities];
      [nextPriorities[index], nextPriorities[nextIndex]] = [
        nextPriorities[nextIndex],
        nextPriorities[index],
      ];

      return {
        ...prev,
        priorities: nextPriorities,
      };
    });
  };

  const handleRemoveAbsenceDraftPriority = (index) => {
    setAbsenceWorkAdjustmentDraft((prev) => ({
      ...prev,
      priorities: prev.priorities.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSaveAbsenceWorkAdjustmentDraft = () => {
    if (!validateAbsenceWorkAdjustmentDraft()) {
      return;
    }

    setAbsenceWorkAdjustmentRows((prev) => {
      const nextRows = [...prev];
      const nextRow = {
        ...absenceWorkAdjustmentDraft,
        priorities: absenceWorkAdjustmentDraft.priorities.map((item) => ({
          ...item,
        })),
      };

      if (editingAbsenceWorkAdjustmentIndex === null) {
        return [...nextRows, nextRow];
      }

      nextRows[editingAbsenceWorkAdjustmentIndex] = nextRow;
      return nextRows;
    });
    setOpenAbsenceWorkAdjustmentDialog(false);
  };

  const handleMoveAbsenceWorkAdjustmentRow = (index, direction) => {
    setAbsenceWorkAdjustmentRows((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) {
        return prev;
      }

      const nextRows = [...prev];
      [nextRows[index], nextRows[nextIndex]] = [
        nextRows[nextIndex],
        nextRows[index],
      ];
      return nextRows;
    });
  };

  const handleDeleteAbsenceWorkAdjustmentRow = (index) => {
    setAbsenceWorkAdjustmentRows((prev) =>
      prev.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const getExampleBoxStyles = (status) => {
    if (status === "Absent") {
      return {
        backgroundColor: "#fff0f0",
        borderColor: "#f8b9b9",
        color: "#FA4A4A",
      };
    }

    if (status === "Week-off") {
      return {
        backgroundColor: "#f1f3f6",
        borderColor: "#c8cdd6",
        color: "#6D7483",
      };
    }

    if (status === "Holiday") {
      return {
        backgroundColor: "#f7e6ef",
        borderColor: "#d7a7be",
        color: "#830046",
      };
    }

    return {
      backgroundColor: "#ecfcf5",
      borderColor: "#b7ebd6",
      color: "#09BD71",
    };
  };

  const renderWeekOffExample = (example = [], optId) => {
    if (!example.length) {
      return null;
    }

    return (
      <Box sx={{ ml: 4, mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.45,
          }}
        >
          {example.map((status, index) => (
            <React.Fragment key={`${optId}-${status}-${index}`}>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.45,
                  borderRadius: 1.25,
                  border: "1px solid",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  ...getExampleBoxStyles(status),
                }}
              >
                {status}
              </Box>
              {index < example.length - 1 && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {"\u2192"}
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box sx={mainOptions}>
            <Box sx={boxStyle(false)}>
              <Typography fontWeight="medium">Is On Duty Allowed</Typography>

              <Switch
                checked={watch("is_onduty_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_onduty_allowed", checked);
                }}
              />
            </Box>
          </Box>
          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_permission_allowed"))}>
              <Typography fontWeight="medium">Is Permission Allowed</Typography>
              <Switch
                checked={watch("is_permission_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_permission_allowed", event.target.checked);
                  if (!checked) {
                    setValue("is_permission_include_working_hour", false);
                    setValue(
                      "is_permission_hours_included_in_the_priority_deduction",
                      false,
                    );
                  }
                }}
              />
            </Box>
            {watch("is_permission_allowed") && (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ ml: 2, mb: 2 }}
                >
                  <Box>
                    <Typography fontWeight="medium">
                      Include in working hours
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Permission hours will be included in total working hours
                      calculation
                    </Typography>
                  </Box>
                  <Box sx={{ mr: 1.6 }}>
                    <Switch
                      checked={watch("is_permission_include_working_hour")}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setValue("is_permission_include_working_hour", checked);

                        if (!checked) {
                          setValue(
                            "is_permission_hours_included_in_the_priority_deduction",
                            false,
                          );
                        }
                      }}
                    />
                  </Box>
                </Box>
                {watch("is_permission_include_working_hour") &&
                  paymentBasedOn === "Full Shift Credit" &&
                  isBandAllowed && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ ml: 2, mb: 2 }}
                    >
                      <Box>
                        <Typography fontWeight="medium">
                          Is permission hours included in the priority deduction
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          permission hours will be included in priority
                          deduction calculation
                        </Typography>
                      </Box>
                      <Box sx={{ mr: 1.6 }}>
                        <Switch
                          checked={watch(
                            "is_permission_hours_included_in_the_priority_deduction",
                          )}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setValue(
                              "is_permission_hours_included_in_the_priority_deduction",
                              checked,
                            );
                          }}
                        />
                      </Box>
                    </Box>
                  )}
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_weekoff_sandwich_leave"))}>
              <Box>
                <Typography fontWeight="medium">
                  Week-off Sandwich Leave
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose when the weekoff rule should be applied if absence
                  occurs.
                </Typography>
              </Box>
              <Switch
                checked={watch("is_weekoff_sandwich_leave")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_weekoff_sandwich_leave", event.target.checked);
                  if (!checked) {
                    setValue("absence_rule_type", "Before");
                  }
                }}
              />
            </Box>
            {watch("is_weekoff_sandwich_leave") && (
              <Box>
                <RadioGroup
                  value={watch("absence_rule_type")}
                  onChange={handlePrefixSuffixForWeekoff}
                >
                  <Grid container spacing={2} sx={{ padding: 2 }}>
                    {prefixSuffixRuleForWeekOff.map((opt) => {
                      const isActive = watch("absence_rule_type") === opt.id;

                      return (
                        <Grid item xs={12} md={6} key={opt.id}>
                          <Box sx={cardStyle(isActive)}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Radio value={opt.id} />
                              <Typography
                                fontWeight="medium"
                                sx={{
                                  color: isActive
                                    ? themes.stepActiveColor
                                    : "inherit",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 4,
                                maxWidth: 250,
                                wordBreak: "break-word",
                              }}
                            >
                              {opt.description}
                            </Typography>
                            {renderWeekOffExample(opt.example, opt.id)}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </RadioGroup>
              </Box>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_holiday_sandwich_leave"))}>
              <Box>
                <Typography fontWeight="medium">
                  Holiday Sandwich Leave
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose when the holiday rule should be applied if absence
                  occurs.
                </Typography>
              </Box>
              <Switch
                checked={watch("is_holiday_sandwich_leave")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_holiday_sandwich_leave", event.target.checked);
                  if (!checked) {
                    setValue("absence_rule_type_for_holiday", "Before");
                  }
                }}
              />
            </Box>
            {watch("is_holiday_sandwich_leave") && (
              <Box>
                <RadioGroup
                  value={watch("absence_rule_type_for_holiday")}
                  onChange={handlePrefixSuffixForHoliday}
                >
                  <Grid container spacing={2} sx={{ padding: 2 }}>
                    {prefixSuffixRuleForHoliday.map((opt) => {
                      const isActive =
                        watch("absence_rule_type_for_holiday") === opt.id;

                      return (
                        <Grid item xs={12} md={6} key={opt.id}>
                          <Box sx={cardStyle(isActive)}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Radio value={opt.id} />
                              <Typography
                                fontWeight="medium"
                                sx={{
                                  color: isActive
                                    ? themes.stepActiveColor
                                    : "inherit",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 4,
                                maxWidth: 250,
                                wordBreak: "break-word",
                              }}
                            >
                              {opt.description}
                            </Typography>
                            {renderWeekOffExample(opt.example, opt.id)}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </RadioGroup>
              </Box>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle(false)}>
              <Typography fontWeight="medium">Is Comp Off Allowed</Typography>

              <Switch
                checked={watch("is_compoff_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_compoff_allowed", checked);
                }}
              />
            </Box>
          </Box>

          {(deductionsData?.is_apply_sft_penalty_policy ||
            deductionsData?.is_apply_break_penalty_policy ||
            deductionsData?.deduct_salary_for_absence) &&
            isBandAllowed && (
              <Box sx={mainOptions}>
                <WorkAdjustmentPriority
                  watch={watch}
                  setValue={setValue}
                  absenceWorkAdjustmentRows={absenceWorkAdjustmentRows}
                  setAbsenceWorkAdjustmentRows={setAbsenceWorkAdjustmentRows}
                  openAbsenceWorkAdjustmentDialog={
                    openAbsenceWorkAdjustmentDialog
                  }
                  setOpenAbsenceWorkAdjustmentDialog={
                    setOpenAbsenceWorkAdjustmentDialog
                  }
                  editingAbsenceWorkAdjustmentIndex={
                    editingAbsenceWorkAdjustmentIndex
                  }
                  absenceWorkAdjustmentDraft={absenceWorkAdjustmentDraft}
                  absenceWorkAdjustmentErrors={absenceWorkAdjustmentErrors}
                  absenceWorkAdjustmentBandOptions={
                    absenceWorkAdjustmentBandOptions
                  }
                  getAvailablePenaltyOptionsForDraft={
                    getAvailablePenaltyOptionsForDraft
                  }
                  openAddAbsenceWorkAdjustmentDialog={
                    openAddAbsenceWorkAdjustmentDialog
                  }
                  openEditAbsenceWorkAdjustmentDialog={
                    openEditAbsenceWorkAdjustmentDialog
                  }
                  handleMoveAbsenceWorkAdjustmentRow={
                    handleMoveAbsenceWorkAdjustmentRow
                  }
                  handleDeleteAbsenceWorkAdjustmentRow={
                    handleDeleteAbsenceWorkAdjustmentRow
                  }
                  handleAbsenceWorkAdjustmentBandChange={
                    handleAbsenceWorkAdjustmentBandChange
                  }
                  handleAddPenaltyToAbsenceDraft={
                    handleAddPenaltyToAbsenceDraft
                  }
                  handleMoveAbsenceDraftPriority={
                    handleMoveAbsenceDraftPriority
                  }
                  handleRemoveAbsenceDraftPriority={
                    handleRemoveAbsenceDraftPriority
                  }
                  handleSaveAbsenceWorkAdjustmentDraft={
                    handleSaveAbsenceWorkAdjustmentDraft
                  }
                />
              </Box>
            )}

          <Box
            sx={{
              ml: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <CustomButton
                actionName="Prev"
                typeName="button"
                actionFuntion={() => {
                  handleBack(2);
                }}
              />
            </Box>
            <Box>
              <CustomButton actionName="Next" typeName="submit" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
